import type { CreasePattern } from '@/origami/types';
import { isFoldable } from '@/origami/types';
import type { DerivedTopology } from '@/origami/topology';
import type { Mat3 } from '@/geometry/rotation';
import {
  crossMatrix,
  mat3Identity,
  mat3Mul,
  mat3Transpose,
  rotationAboutAxis,
  rotationLog,
  veeSkew,
} from '@/geometry/rotation';
import { heading2, sub2 } from '@/geometry/vec2';
import type { Matrix } from '@/geometry/linalg';
import { zeros } from '@/geometry/linalg';

/**
 * Rigid-origami loop-closure constraint system (Tachi's formulation).
 *
 * For every interior vertex v of degree n, let u_1 ... u_n be the unit
 * directions of its incident creases in counter-clockwise order (in the flat
 * pattern) and rho_1 ... rho_n their fold angles. Walking once around the
 * vertex composes one rotation per crease, so the sheet closes up iff
 *
 *     M_v(rho) = R(u_1, rho_1) R(u_2, rho_2) ... R(u_n, rho_n) = I .
 *
 * (Expanding R(u_k, rho) = Rz(phi_k) Rx(rho) Rz(-phi_k) and telescoping gives
 * the more familiar  prod_i Rx(rho_i) Rz(alpha_i) = I  with alpha_i the sector
 * angles; the form above is used here because its sign convention is the same
 * one the 3D reconstruction uses, so the two can never drift apart.)
 *
 * That is three independent scalar equations per interior vertex. We take the
 * residual as log(M_v) in so(3), which vanishes exactly when M_v = I.
 *
 * The derivative is exact:  dM/drho_k = P_{k-1} [u_k]x R_k S_{k+1}, and the
 * corresponding constraint velocity is  vee( (dM/drho_k) M^T ). Composing with
 * the (invertible) right-Jacobian of the log map does not change the rank, so
 * this Jacobian is the right object for degree-of-freedom counting.
 *
 * Reference: T. Tachi, "Simulation of Rigid Origami", Origami^4 (2009).
 */
export interface RigidSystem {
  /** Edge id for each fold-angle variable. */
  variableEdges: number[];
  /** variable index for each edge, or -1 if the edge is not a variable. */
  edgeVariable: Int32Array;
  /** Interior vertices that contribute constraints. */
  constraintVertices: number[];
  /** For each constraint vertex, its crease directions (unit, CCW). */
  vertexAxes: { x: number; y: number; z: number }[][];
  /** For each constraint vertex, the variable index of each incident crease. */
  vertexVars: number[][];
  variableCount: number;
  constraintCount: number;
}

export function buildRigidSystem(
  pattern: CreasePattern,
  topo: DerivedTopology,
): RigidSystem {
  const edgeVariable = new Int32Array(pattern.edgesVertices.length).fill(-1);
  const variableEdges: number[] = [];
  for (const e of topo.interiorEdges) {
    if (!isFoldable(pattern.edgesAssignment[e])) continue;
    edgeVariable[e] = variableEdges.length;
    variableEdges.push(e);
  }

  const constraintVertices: number[] = [];
  const vertexAxes: { x: number; y: number; z: number }[][] = [];
  const vertexVars: number[][] = [];

  for (const v of topo.vertices) {
    if (!v.interior || v.degree < 3) continue;
    const vars = v.edges.map((e) => edgeVariable[e]);
    if (vars.some((i) => i < 0)) continue; // a non-foldable crease at an interior vertex
    const axes = v.neighbours.map((n) => {
      const d = sub2(pattern.verticesCoords[n], pattern.verticesCoords[v.index]);
      const a = heading2(d);
      return { x: Math.cos(a), y: Math.sin(a), z: 0 };
    });
    constraintVertices.push(v.index);
    vertexAxes.push(axes);
    vertexVars.push(vars);
  }

  return {
    variableEdges,
    edgeVariable,
    constraintVertices,
    vertexAxes,
    vertexVars,
    variableCount: variableEdges.length,
    constraintCount: constraintVertices.length * 3,
  };
}

export interface ResidualAndJacobian {
  residual: Float64Array;
  jacobian: Matrix;
  /** Infinity norm of the residual, radians. */
  maxResidual: number;
  /** Euclidean norm of the residual. */
  normResidual: number;
}

export function evaluateRigidSystem(
  system: RigidSystem,
  rho: Float64Array,
  wantJacobian = true,
): ResidualAndJacobian {
  const m = system.constraintCount;
  const n = system.variableCount;
  const residual = new Float64Array(m);
  const jacobian = zeros(wantJacobian ? m : 0, wantJacobian ? n : 0);

  for (let vi = 0; vi < system.constraintVertices.length; vi++) {
    const axes = system.vertexAxes[vi];
    const vars = system.vertexVars[vi];
    const k = axes.length;

    const rots: Mat3[] = new Array(k);
    for (let i = 0; i < k; i++) rots[i] = rotationAboutAxis(axes[i], rho[vars[i]]);

    // Prefix products: prefix[i] = R_0 ... R_{i-1}
    const prefix: Mat3[] = new Array(k + 1);
    prefix[0] = mat3Identity();
    for (let i = 0; i < k; i++) prefix[i + 1] = mat3Mul(prefix[i], rots[i]);
    // Suffix products: suffix[i] = R_i ... R_{k-1}
    const suffix: Mat3[] = new Array(k + 1);
    suffix[k] = mat3Identity();
    for (let i = k - 1; i >= 0; i--) suffix[i] = mat3Mul(rots[i], suffix[i + 1]);

    const M = prefix[k];
    const c = rotationLog(M);
    const row = vi * 3;
    residual[row] = c.x;
    residual[row + 1] = c.y;
    residual[row + 2] = c.z;

    if (!wantJacobian) continue;
    const Mt = mat3Transpose(M);
    for (let i = 0; i < k; i++) {
      // dM/drho_i = prefix[i] * [u_i]x * R_i * suffix[i+1]
      const dR = mat3Mul(crossMatrix(axes[i]), rots[i]);
      const dM = mat3Mul(mat3Mul(prefix[i], dR), suffix[i + 1]);
      const omega = veeSkew(mat3Mul(dM, Mt));
      const col = vars[i];
      jacobian.data[row * n + col] += omega.x;
      jacobian.data[(row + 1) * n + col] += omega.y;
      jacobian.data[(row + 2) * n + col] += omega.z;
    }
  }

  let maxResidual = 0;
  let sum = 0;
  for (let i = 0; i < m; i++) {
    const a = Math.abs(residual[i]);
    if (a > maxResidual) maxResidual = a;
    sum += residual[i] * residual[i];
  }

  return { residual, jacobian, maxResidual, normResidual: Math.sqrt(sum) };
}
